import {connect} from 'react-redux';

import {ViewerPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {
  isActivityInConversationRelevant,
  isCurrentUserAuthor,
} from '../../../../helpers/models/conversationHelpers';
import {ActivityModel} from '../../../../models/api/activityModel';
import {AppState} from '../../../../store/appState';
import {jumpToActivityAsyncAction} from '../../../../store/panels/viewer/viewerPanelActions';
import {ViewerPanelConversationState} from '../../../../store/panels/viewer/viewerPanelState';
import {UnreadBannerAbove} from './unreadBannerAbove';

/*
 * Props.
 */

interface UnreadBannerAboveContainerProps {
  panelId: ViewerPanelId;
  originalConversationState: ViewerPanelConversationState | undefined;
  userId: number;
  activities: ReadonlyArray<ActivityModel>;
  readActivityIds: ReadonlyArray<string>;
  firstVisibleActivityDate: number | undefined; // Earliest activity visible in the viewport.
  originalLastActivityDate: number;
}

/*
 * Selectors.
 */

function getActivities(_state: AppState, props: UnreadBannerAboveContainerProps) {
  return props.activities;
}

function getReadActivityIds(_state: AppState, props: UnreadBannerAboveContainerProps) {
  return props.readActivityIds;
}

function getUserId(_state: AppState, props: UnreadBannerAboveContainerProps) {
  return props.userId;
}

function getFirstVisibleActivityDate(_state: AppState, props: UnreadBannerAboveContainerProps) {
  return props.firstVisibleActivityDate;
}

function getOriginalLastActivityDate(_state: AppState, props: UnreadBannerAboveContainerProps) {
  return props.originalLastActivityDate;
}

// Unread activities that occurred after the conversation initially loaded.
const makeGetUnreadActivitiesAbove = () =>
  createSelector(
    [getActivities, getReadActivityIds, getUserId, getFirstVisibleActivityDate, getOriginalLastActivityDate],
    (activities, readActivityIds, userId, firstVisibleActivityDate, originalLastActivityDate) =>
      activities.filter(
        (activity) =>
          isActivityInConversationRelevant(activity) &&
          !isCurrentUserAuthor(activity, userId) &&
          !readActivityIds.includes(`${activity.id}`) &&
          (!firstVisibleActivityDate || activity.date < firstVisibleActivityDate) &&
          activity.date > originalLastActivityDate,
      ),
  );

const mapStateToProps = makeMapStateToProps<UnreadBannerAboveContainerProps>()(() => {
  const getUnreadActivitiesAbove = makeGetUnreadActivitiesAbove();
  return {
    unreadActivitiesAbove: getUnreadActivitiesAbove,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<UnreadBannerAboveContainerProps>()((props) => ({
  onUnreadBannerClick: (activityId: number | undefined) =>
    jumpToActivityAsyncAction(
      props.panelId,
      activityId === undefined ? {date: props.originalConversationState?.seenAt ?? 0} : {id: activityId},
    ),
}));

/*
 * Component.
 */

export const UnreadBannerAboveContainer = connect(mapStateToProps, mapDispatchToProps)(UnreadBannerAbove);
